import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { Form, Formik, useField } from 'formik';
import { useState } from 'react';
import { Button, Card, Modal } from 'react-bootstrap';
import CardHeader from 'react-bootstrap/esm/CardHeader';
import Textarea from '../Input/Textarea';
import { deleteOnboarding } from '../../services/commerce.service';
import { triggerToasts } from '../../utils/helpers';
import schemas from '../../utils/schemas';

interface DeleteOnboardingValues {
  reason: string;
}

interface DeleteOnboardingModalProps {
  show: boolean;
  businessId: number | null;
  onCancel: () => void;
  onDeleted: () => void;
}

function ReasonHint() {
  const [, meta] = useField('reason');
  const hasError = !!meta.error && meta.touched;

  return (
    <span className={clsx('reason-hint', hasError && 'has-error')}>
      <FontAwesomeIcon icon={faCircleInfo as IconProp} />
      Debe tener al menos {schemas.deleteOnboardingReasonMinLength} caracteres
    </span>
  );
}

function ConfirmButton({ isLoading }: { isLoading: boolean }) {
  const [field] = useField('reason');
  const isReasonTooShort = field.value.trim().length < schemas.deleteOnboardingReasonMinLength;

  return (
    <Button
      variant="outline-danger"
      className="primary-outlined-button"
      type="submit"
      disabled={isLoading || isReasonTooShort}
    >
      Confirmar &gt;
    </Button>
  );
}

function DeleteOnboardingModal({ show, businessId, onCancel, onDeleted }: DeleteOnboardingModalProps) {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSubmit = async (values: DeleteOnboardingValues) => {
    if (!businessId) return;

    setIsLoading(true);
    const response = await deleteOnboarding(businessId, { reason: values.reason.trim() });
    setIsLoading(false);

    if (response?.result) {
      triggerToasts({ status: 200, message: 'Onboarding eliminado correctamente' });
      return onDeleted();
    }

    triggerToasts(
      response?.data?.message || response?.message || 'Ha ocurrido un error al eliminar el onboarding',
    );
  };

  return (
    <Modal show={show} onHide={onCancel} centered dialogClassName="delete-onboarding-modal">
      <Card className="modal-card delete-onboarding-card">
        <CardHeader>
          <h1>Eliminar onboarding</h1>
        </CardHeader>
        <Card.Body>
          <p className="delete-onboarding-description">
            Esta acción no se puede deshacer. Escribe una breve descripción sobre por qué lo quieres eliminar.
          </p>
          <Formik
            initialValues={{ reason: '' }}
            validationSchema={schemas.DeleteOnboardingSchema}
            onSubmit={handleSubmit}
          >
            <Form>
              <Textarea
                name="reason"
                label="Motivo"
                required
                hideError
                placeholder="Ej: el cliente canceló el proceso antes de firmar"
              />
              <ReasonHint />
              <div className="delete-onboarding-actions">
                <Button
                  variant="outline-danger"
                  className="primary-outlined-button"
                  onClick={onCancel}
                  disabled={isLoading}
                >
                  &lt; Cancelar
                </Button>
                <ConfirmButton isLoading={isLoading} />
              </div>
            </Form>
          </Formik>
        </Card.Body>
      </Card>
    </Modal>
  );
}

export default DeleteOnboardingModal;
